import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { API_BASE } from "@/lib/api";
import { AnswerValue } from "@/components/form";
import { Deadline, StatePill } from "@/components/pipeline";
import {
  applicantQuery,
  useAddNote,
  useDecide,
  type Applicant,
  type Outcome,
} from "@/lib/coord";
import {
  Button,
  Callout,
  Card,
  Empty,
  Input,
  Label,
  Pill,
  Textarea,
} from "@/components/ui";
import { Select } from "@/components/pick";

export const Route = createFileRoute("/_auth/coord/$vertical/applicants/$id")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(applicantQuery(params.vertical, params.id)),
  component: ApplicantPage,
});

/**
 * One applicant, everything they wrote, and the decision.
 *
 * The resume is a plain link to the authenticated file route — the same route
 * that refuses a coordinator of a vertical this person did not apply to. No
 * signed URL, no fetch-and-blob: the browser's own cookie is exactly the right
 * credential, and the check runs on every request.
 */
function ApplicantPage() {
  const { vertical, id } = Route.useParams();
  const { data } = useSuspenseQuery(applicantQuery(vertical, id));
  const current = data.rounds.find((r) => r.current) ?? null;

  return (
    <div className="flex flex-col gap-10">
      <Link to="/coord/$vertical" params={{ vertical }} className="mono text-ink-3 hover:text-ink">
        ← Applicants
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-col gap-3">
          <h2 className="display text-[clamp(26px,4vw,36px)]">{data.user.name}</h2>
          <p className="mono text-ink-3">
            {data.user.bitsId ?? "no BITS ID"}
            {data.user.admissionYear ? ` · ${data.user.admissionYear} intake` : ""}
            {data.profile.branch ? ` · ${data.profile.branch}` : ""}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <a
              href={`mailto:${data.user.email}`}
              className="text-[15px] underline underline-offset-4 hover:text-[color:var(--accent-text)]"
            >
              {data.user.email}
            </a>
            {data.profile.phone ? (
              <span className="text-[15px] text-ink-2">{data.profile.phone}</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col items-end gap-3">
          <StatePill state={data.application.state} />
          {data.alsoSelectedBy.length > 0 ? (
            <Pill tone="good">Also selected by {data.alsoSelectedBy.join(", ")}</Pill>
          ) : null}
        </div>
      </header>

      <section className="flex flex-col gap-5">
        <h3 className="mono text-ink-3">Profile</h3>
        <Card className="flex flex-col gap-6">
          {data.profile.about ? (
            <p className="max-w-[62ch] whitespace-pre-wrap text-[15px] leading-relaxed text-ink-2">
              {data.profile.about}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {data.profile.resume ? (
              <a
                href={`${API_BASE}/files/${data.profile.resume.id}`}
                target="_blank"
                rel="noreferrer"
                className="mono underline underline-offset-4 hover:text-[color:var(--accent-text)]"
              >
                {data.profile.resume.filename}
              </a>
            ) : (
              <span className="mono text-ink-3">No resume</span>
            )}
            {data.profile.links.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noreferrer nofollow"
                className="mono text-ink-3 underline underline-offset-4 hover:text-ink"
              >
                {link.kind}
              </a>
            ))}
          </div>
        </Card>
      </section>

      {current ? (
        <DecideOne vertical={vertical} id={data.application.id} round={current} />
      ) : null}

      <section className="flex flex-col gap-5">
        <h3 className="mono text-ink-3">Rounds</h3>
        <div className="flex flex-col gap-5">
          {data.rounds.map((round) => (
            <RoundBlock key={round.id} round={round} />
          ))}
        </div>
      </section>

      <Notes vertical={vertical} id={data.application.id} notes={data.notes} />
    </div>
  );
}

type RoundBlockData = Applicant["rounds"][number];

function RoundBlock({ round }: { round: RoundBlockData }) {
  const labels = new Map(round.questions.map((q) => [q.id, q]));
  const answers = (round.submission?.answers ?? {}) as Record<string, unknown>;
  // In the FORM's order, not the blob's. `answers` is jsonb, and Postgres
  // stores a jsonb object with its keys sorted by length — so reading
  // `Object.keys` back gives an order nobody chose, and the coordinator reads
  // the third question first. Anything left over is an answer to a question
  // that has since been retired: it goes at the end rather than disappearing.
  const asked = round.questions.map((q) => q.id).filter((id) => id in answers);
  const retired = Object.keys(answers).filter((id) => !labels.has(id));
  const keys = [...asked, ...retired];

  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h4 className="display text-[20px]">{round.name}</h4>
          {round.current ? <Pill tone="live">Here now</Pill> : null}
          {round.submission?.late ? <Pill tone="warn">Late</Pill> : null}
        </div>
        <div className="flex items-center gap-4">
          <Deadline at={round.closesAt} late={round.submission?.late} />
          {round.result && round.result.outcome !== "pending" ? (
            <Pill tone={round.result.outcome === "rejected" ? "quiet" : "good"}>
              {round.result.outcome}
            </Pill>
          ) : null}
        </div>
      </div>

      {round.result?.note ? (
        <p className="mono text-ink-3">Note sent: {round.result.note}</p>
      ) : null}

      {!round.submission ? (
        <p className="text-[14px] text-ink-3">Nothing submitted for this round.</p>
      ) : keys.length === 0 ? (
        <p className="text-[14px] text-ink-3">
          Submitted, with no answers — this round has no form.
        </p>
      ) : (
        <dl className="flex flex-col gap-5">
          {keys.map((qid) => {
            const q = labels.get(qid);
            return (
              <div key={qid} className="flex flex-col gap-1.5">
                <dt className="mono text-ink-3">
                  {q?.label ?? "Retired question"}
                  {q?.archived ? " · archived" : ""}
                </dt>
                <dd>
                  <AnswerValue value={answers[qid]} />
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </Card>
  );
}

/** The decision on the round this person is actually in. */
function DecideOne({
  vertical,
  id,
  round,
}: {
  vertical: string;
  id: string;
  round: RoundBlockData;
}) {
  const decide = useDecide(vertical);
  const [outcome, setOutcome] = useState<Outcome>("advanced");
  const [note, setNote] = useState("");

  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="mono text-ink-3">Decide — {round.name}</h3>
        <p className="text-[14px] text-ink-3">
          {round.submission
            ? "They have submitted. It is with you."
            : "Nothing submitted for this round yet."}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[13rem] flex-col gap-2">
          <Label htmlFor="one-outcome">Outcome</Label>
          <Select
            id="one-outcome"
            value={outcome}
            options={[
              { value: "advanced", label: "Advance" },
              { value: "selected", label: "Select — they are in" },
              { value: "rejected", label: "Not this time" },
            ]}
            onChange={setOutcome}
          />
        </div>
        <div className="flex min-w-[16rem] flex-1 flex-col gap-2">
          <Label htmlFor="one-note">Note to them (optional)</Label>
          <Input
            id="one-note"
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <Button
          disabled={decide.isPending}
          onClick={() =>
            decide.mutate(
              { applicationIds: [id], roundId: round.id, outcome, note },
              { onSuccess: () => setNote("") },
            )
          }
        >
          {decide.isPending ? "Recording" : "Record"}
        </Button>
      </div>

      {decide.error ? <Callout tone="error">{(decide.error as Error).message}</Callout> : null}
    </Card>
  );
}

/**
 * Private notes.
 *
 * These never leave the API — there is no route anywhere that shows an
 * application note to the person it is about, which is exactly why a
 * coordinator can write honestly in one.
 */
function Notes({
  vertical,
  id,
  notes,
}: {
  vertical: string;
  id: string;
  notes: Applicant["notes"];
}) {
  const add = useAddNote(vertical, id);
  const [body, setBody] = useState("");

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="mono text-ink-3">Notes</h3>
        <span className="mono text-ink-3">Never shown to the applicant</span>
      </div>

      <div className="flex flex-col gap-4">
        <Textarea
          rows={3}
          value={body}
          maxLength={2000}
          placeholder="What you want the next person reading this to know."
          onChange={(e) => setBody(e.target.value)}
        />
        <div>
          <Button
            className="h-9 px-3"
            disabled={!body.trim() || add.isPending}
            onClick={() => add.mutate(body.trim(), { onSuccess: () => setBody("") })}
          >
            {add.isPending ? "Saving" : "Add note"}
          </Button>
        </div>
        {add.error ? <Callout tone="error">{(add.error as Error).message}</Callout> : null}
      </div>

      {notes.length === 0 ? (
        <Empty>No notes yet.</Empty>
      ) : (
        <ul className="flex flex-col gap-4">
          {notes.map((note) => (
            <li key={note.id} className="flex flex-col gap-1.5 border-l-2 border-line pl-4">
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{note.body}</p>
              <p className="mono text-ink-3">
                {note.author} ·{" "}
                {note.createdAt
                  ? new Date(note.createdAt).toLocaleString("en-IN", {
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
